/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2014 - 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/
// A helper function to allow an Angular Service to be accessible from AngularJs.
import { downgradeComponent, downgradeInjectable } from '@angular/upgrade/static';

// import configs here:
import { SvgIconComponent } from '@ngneat/svg-icon';
import { BulbUtilitiesService } from 'src/common/services/bulb-utilities.service';
import { MenuListComponent } from 'src/app/menus/menu-list/menu-list.component';
import { RecordEventService } from 'src/app/telemetry/record-event.service';
import { TilesFooterComponent } from 'src/app/tiles/tiles-footer/tiles-footer.component';
import { PageViewLikesComponent } from 'src/app/page-view-footer/page-view-likes/page-view-likes.component';
import { UppyDashboardService } from 'src/app/asset-uploads/uppy-dashboard.service';
import { AssetUploadEventService } from 'src/app/asset-uploads/asset-upload-event.service';
import { AssetUploadHttpService } from 'src/app/asset-uploads/http.service';
import { AssignmentHttpService } from 'src/app/assignment/assignment-http.service';
import { LoggedOutService } from '../app/helpers/logged-out.service';
import { angularjsAppConfig, angularRunBlock, jqueryUiConfig } from './app-config';

// A list of our current components. We will have to update this list as we incrementally convert our other AngularJs
// modules into Angular components.
import bootstrapComponent from './bootstrap/bootstrap-component';
import groupBlockDetailsComponent from './components/group/group-block-details-component';
import groupCreateComponent from './components/group/group-create-component';
import groupHeaderComponent from './components/group/group-header-component';
import headerComponent from './header/header-component';

// Angular Components that we need to downgrade in order for them to render and function in the AngularJS framework:
import { AngularTestingTranslations } from '../app/angular-testing-translations/angular-testing-translations.component';
import { CompositeViewMenusComponent } from '../app/composite-view/composite-view-menus/composite-view-menus.component';
import { FamilyMemberContainerComponent }
  from '../family-member/family-member-container/family-member-container.component';
import { DevToolsComponent } from '../app/dev-tools/dev-tools.component';
import { ResumeComponent } from '../resume/resume.component';

import { AeroAccordionComponent } from '../aero/aero-accordion/aero-accordion.component';
import { AeroAccordionTabComponent } from '../aero/aero-accordion/aero-accordion-tab/aero-accordion-tab.component';
import { AeroButtonComponent } from '../aero/aero-button/aero-button.component';
import { AeroCalendarPickerComponent } from '../aero/aero-calendar-picker/aero-calendar-picker.component';
import { AeroDropdownComponent } from '../aero/aero-dropdown/aero-dropdown.component';
import { AeroDialogService } from '../aero/aero-dialog/aero-dialog.service';
import { AeroFormComponent } from '../aero/aero-form/aero-form.component';
import { AeroInputComponent } from '../aero/aero-input/aero-input.component';
import { AeroTextareaComponent } from '../aero/aero-textarea/aero-textarea.component';
import { AnalyticsService } from '../common/analytics.service';
import { AssignmentService } from '../app/assignment/assignment.service';
import {
  AssetLibraryUploadButtonComponent,
} from '../app/asset-uploads/asset-library-upload-button/asset-library-upload-button.component';
import { AccountSettingsDialogService } from '../app/account-settings-dialog/account-settings-dialog.service';
import { AudioCompatibilityService } from '../app/audio/audio-compatibility.service';
import { BulbCKEStateService } from '../ckeditor/editor/services/bulb-cke-state.service';
import { BulbFetchUserService } from '../common/fetch-user.service';
import { BulbFetchMediaDataService } from '../app/audio/fetch-media-data.service';
import { UserAvatarComponent } from '../app/avatar/user-avatar/user-avatar.component';
import { EditorComponent } from '../ckeditor/editor/editor.component';
import { ErrorComponent } from '../common/errors/error.component';
import { ErrorService } from '../common/error.service';
import { FamilyMemberService } from '../family-member/family-member.service';
import { GroupHttpService } from '../app/group/group.service';
import { FeatureFlagService } from '../common/feature-flag.service';
import { HttpService } from '../app/share-panel/http.service';
import { ImageCarouselComponent } from '../app/image-carousel/image-carousel.component';
import { LaunchButtonComponent } from '../app/mobile-launcher/launch-button/launch-button.component';
import { MenuListPanelComponent } from '../app/menus/menu-list-panel/menu-list-panel.component';
import { MobileLaunchToastComponent } from '../app/mobile-launcher/mobile-launch-toast/mobile-launch-toast.component';
import { OfflineNotificationComponent } from '../app/offline/offline-notification/offline-notification.component';
import { PlanListComponent } from '../app/buy-page/plan-list.component';
import { PageEditorComponent } from '../ckeditor/editor/page-editor/page-editor.component';
import {
  PageViewHeaderMenusComponent,
} from '../app/page-view-header/page-view-header-menus/page-view-header-menus.component';
import { SaveStateStore } from '../save/save-state.store';
import { SerialSaveService } from '../save/serial-save.service';
import { ResumeService } from '../resume/resume.service';
import { ShareButtonComponent } from '../app/share-panel/share-button/share-button.component';
import { TourService } from '../app/tour/tour.service';
import { UnitStateStore } from '../unit/unit-state.store';
import { UploadAssetService } from '../common/upload-asset.service';
import { PageHeaderComponent } from '../app/page-view/page-header/page-header.component';
import { DeleteAccountDialogService } from '../app/account-settings-dialog/delete-account-dialog/delete-account-dialog.service';
import './app.module.imports.ajs';
import {
  ImageCropperDialogService,
} from '../app/image-cropper/image-cropper-dialog.service';
import { ShutdownAlertComponent } from '../app/alert/shutdown-alert/shutdown-alert.component';
import { PinnedUnitsComponent } from '../app/group/pinned-units/pinned-units.component';
import { SearchFormComponent } from '../app/search/search-form.component';
import { TagsComponent } from '../app/organization/tags/tags.component';
import { AccountSettingsComponent } from '../app/account-settings/account-settings.component';
import { AccountVerifyEmailComponent } from '../app/account-settings/account-verify-email.component';

// This is the AngularJs root module used to bootstrap all of our other AngularJs modules that have yet to be converted
// Over to Angular (ng2+).

// Our AngularJs Module named, as a const variable.
const MODULE_NAME = 'bulbApp';

const TEMPLATE_MODULE_NAME = 'bulbApp.cacheTemplateModule';

// FUTURE: Would really love to remove the unnecessary dependencies here. It isn't necessary that our root module depend
// On all of these.
angular.module(MODULE_NAME, [
  'ngAnimate', 'ngAria', 'ngCookies', 'ngDragDrop', 'ngResource', 'ngSanitize', 'angular-inview',
  'ngclipboard', 'mm.foundation', 'restangular', 'ui.router', 'ui.router.title', 'ui.sortable',
  TEMPLATE_MODULE_NAME, 'bulbApp.accountModule', 'bulbApp.adminModule', 'bulbApp.ageVerificationModule',
  'bulbApp.ajaxModule', 'bulbApp.animationModule', 'bulbApp.applicationModule', 'bulbApp.assetLibraryModule',
  'bulbApp.authModule', 'bulbApp.authorModule', 'bulbApp.blockModule', 'bulbApp.bootstrapModule',
  'bulbApp.checkboxModule',
  'bulbApp.compositeViewModule', 'bulbApp.configModule', 'bulbApp.devToolsModule', 'bulbApp.explorerModule',
  'bulbApp.foundationModule', 'bulbApp.groupModule', 'bulbApp.headerModule', 'bulbApp.inappropriateContentModule',
  'bulbApp.modalModule', 'bulbApp.offBoardingModule', 'bulbApp.organizationModule',
  'bulbApp.pageViewModule', 'bulbApp.presentationModule', 'bulbApp.radiobuttonModule', 'bulbApp.recommendModule',
  'bulbApp.searchModule', 'bulbApp.translateModule', 'bulbApp.unitModule', 'bulbApp.validationModule',
])

  // Essential AngularJS Application configuration which are imported above from our app-config.ts file:
  .config(angularjsAppConfig)
  .run(angularRunBlock)
  .value(jqueryUiConfig)

  // Register the current list of components that were converted from AngularJS modules.
  .component('bootstrapComponent', bootstrapComponent)
  .component('bulbGroupBlockDetails', groupBlockDetailsComponent)
  .component('bulbGroupCreate', groupCreateComponent)
  .component('bulbGroupHeader', groupHeaderComponent)
  .component('bulbHeader', headerComponent)

  // Downgraded Angular component
  .directive('aeroAccordion', downgradeComponent({ component: AeroAccordionComponent }))
  .directive('aeroAccordionTab', downgradeComponent({ component: AeroAccordionTabComponent }))
  .directive('aeroButton', downgradeComponent({ component: AeroButtonComponent }))
  .directive('aeroCalendarPicker', downgradeComponent({ component: AeroCalendarPickerComponent }))
  .directive('aeroDropdown', downgradeComponent({ component: AeroDropdownComponent }))
  .directive('aeroForm', downgradeComponent({ component: AeroFormComponent }))
  .directive('aeroInput', downgradeComponent({ component: AeroInputComponent }))
  .directive('aeroTextarea', downgradeComponent({ component: AeroTextareaComponent }))
  .directive('angularTranslation', downgradeComponent({ component: AngularTestingTranslations }))
  .directive('angularDevToolsComponent', downgradeComponent({ component: DevToolsComponent }))
  .directive('bulbEditor', downgradeComponent({ component: EditorComponent }))
  .directive('bulbError', downgradeComponent({ component: ErrorComponent }))
  .directive('bulbPageEditor', downgradeComponent({ component: PageEditorComponent }))
  .directive('familyMemberContainer', downgradeComponent({ component: FamilyMemberContainerComponent }))
  .directive('bulbimageCarousel', downgradeComponent({ component: ImageCarouselComponent }))
  .directive('bulbMobileLaunchButton', downgradeComponent({ component: LaunchButtonComponent }))
  .directive('bulbMobileLaunchToast', downgradeComponent({ component: MobileLaunchToastComponent }))
  .directive('bulbPinnedUnits', downgradeComponent({ component: PinnedUnitsComponent }))
  .directive('bulbPlanList', downgradeComponent({ component: PlanListComponent }))
  .directive('bulbShutdownAlert', downgradeComponent({ component: ShutdownAlertComponent }))
  .directive('bulbResume', downgradeComponent({ component: ResumeComponent }))
  .directive('bulbShareButton', downgradeComponent({ component: ShareButtonComponent }))
  .directive('bulbCompositeViewMenus', downgradeComponent({ component: CompositeViewMenusComponent }))
  .directive('bulbPageViewHeaderMenus', downgradeComponent({ component: PageViewHeaderMenusComponent }))
  .directive('bulbUserAvatar', downgradeComponent({ component: UserAvatarComponent }))
  .directive('bulbPageHeader', downgradeComponent({ component: PageHeaderComponent }))
  .directive('bulbMenuListPanel', downgradeComponent({ component: MenuListPanelComponent }))
  .directive('bulbMenuList', downgradeComponent({ component: MenuListComponent }))
  .directive('svgIcon', downgradeComponent({ component: SvgIconComponent }))
  .directive('bulbTags', downgradeComponent({ component: TagsComponent }))
  .directive('bulbTilesFooter', downgradeComponent({ component: TilesFooterComponent }))
  .directive('bulbOfflineNotification', downgradeComponent({ component: OfflineNotificationComponent }))
  .directive('bulbAssetLibraryUploadButton', downgradeComponent({ component: AssetLibraryUploadButtonComponent }))
  .directive('bulbPageViewLikes', downgradeComponent({ component: PageViewLikesComponent }))
  .directive('bulbSearchFormApp', downgradeComponent({ component: SearchFormComponent }))
  .directive('bulbAccountSettings', downgradeComponent({ component: AccountSettingsComponent }))
  .directive('bulbAccountVerifyEmail', downgradeComponent({ component: AccountVerifyEmailComponent }))

  // Downgraded Angular Services so that AngularJS modules can inject them
  .factory('AccountSettingsDialogService', downgradeInjectable(AccountSettingsDialogService))
  .factory('ImageCropperDialogService', downgradeInjectable(ImageCropperDialogService))
  .factory('AeroDialogService', downgradeInjectable(AeroDialogService))
  .factory('AnalyticsService', downgradeInjectable(AnalyticsService))
  .factory('AssignmentService', downgradeInjectable(AssignmentService))
  .factory('AssignmentHttpService', downgradeInjectable(AssignmentHttpService))
  .factory('AudioCompatibilityService', downgradeInjectable(AudioCompatibilityService))
  .factory('AssetUploadEventService', downgradeInjectable(AssetUploadEventService))
  .factory('AssetUploadHttpService', downgradeInjectable(AssetUploadHttpService))
  .factory('BulbCKEStateService', downgradeInjectable(BulbCKEStateService))
  .factory('BulbFetchUserService', downgradeInjectable(BulbFetchUserService))
  .factory('BulbFetchMediaDataService', downgradeInjectable(BulbFetchMediaDataService))
  .factory('BulbUtilitiesService', downgradeInjectable(BulbUtilitiesService))
  .factory('DeleteAccountDialogService', downgradeInjectable(DeleteAccountDialogService))
  .factory('ErrorService', downgradeInjectable(ErrorService))
  .factory('FamilyMemberService', downgradeInjectable(FamilyMemberService))
  .factory('FeatureFlagService', downgradeInjectable(FeatureFlagService))
  .factory('GroupHttpService', downgradeInjectable(GroupHttpService))
  .factory('HttpService', downgradeInjectable(HttpService))
  .factory('LoggedOutService', downgradeInjectable(LoggedOutService))
  .factory('RecordEventService', downgradeInjectable(RecordEventService))
  .factory('ResumeService', downgradeInjectable(ResumeService))
  .factory('SaveStateStore', downgradeInjectable(SaveStateStore))
  .factory('SerialSaveService', downgradeInjectable(SerialSaveService))
  .factory('TourService', downgradeInjectable(TourService))
  .factory('UnitStateStore', downgradeInjectable(UnitStateStore))
  .factory('UploadAssetService', downgradeInjectable(UploadAssetService))
  .factory('UppyDashboardService', downgradeInjectable(UppyDashboardService));
export default MODULE_NAME;

/**
 * Fetch the template AJS module generated at built-time.
 * During build-time, Webpack generates a templates module that contains all the templates that are
 * used by AngularJS directives (components) and views in a single file.
 * This module is generated before the app is built, so the file is available at run-time.
 *
 * During development, we want our templates to update as we make changes to the code.
 * So, we do not load the template module in the development build.
 * Instead, we create an empty module, which prevents AJS throwing an error.
 *
 * Each directive will request its own template .HTML file.
 */

export async function fetchAJSTemplates() {
  if (process.env.PROD_MODE) {
    // @ts-ignore
    await import('AJSTemplateCache');
  } else {
    angular.module(TEMPLATE_MODULE_NAME, []);
  }
}
